import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../api/auth';
import { isTwoFactorRequired, LoginResponse } from '../types';
import { TwoFactorVerify } from '../components/auth/TwoFactorVerify';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);

  const { completeLogin, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Basic validation
    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApi.login({ email, password });

      if (isTwoFactorRequired(response)) {
        // 2FA is required - store temp token and show verification screen
        setTempToken(response.temp_token);
        setRequires2FA(true);
      } else {
        // Normal login - complete the auth flow
        await completeLogin(response);
        // Navigation will happen via useEffect when isAuthenticated becomes true
      }
    } catch (err: unknown) {
      let errorMessage = 'Login failed';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string | Array<{ msg: string }> } } };
        const detail = axiosError.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail[0].msg || 'Validation error';
        }
      }
      setLocalError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FASuccess = async (response: LoginResponse) => {
    try {
      await completeLogin(response);
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (err) {
      console.error('Failed to complete login after 2FA:', err);
      setLocalError('Failed to complete login');
      setRequires2FA(false);
      setTempToken(null);
    }
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTempToken(null);
    setPassword('');
  };

  const displayError = localError || error;
  const isFormDisabled = isSubmitting || isLoading;

  // Show 2FA verification screen
  if (requires2FA && tempToken) {
    return (
      <div className="auth-layout">
        <Card className="auth-card">
          <div className="auth-header">
            <img src="/tallai-icon.png" alt="TallAI" />
            <h1>tall<b>ai</b></h1>
          </div>

          <TwoFactorVerify
            tempToken={tempToken}
            onSuccess={handle2FASuccess}
            onCancel={handle2FACancel}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <Card className="auth-card">
        <div className="auth-header">
          <img src="/tallai-icon.png" alt="tallai" />
          <h1>tall<b>ai</b></h1>
        </div>

        {displayError && (
          <Message severity="error" text={displayError} className="form-error" />
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="field">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              disabled={isFormDisabled}
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <Password
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              toggleMask
              feedback={false}
              autoComplete="current-password"
              disabled={isFormDisabled}
            />
          </div>

          <div className="field-checkbox">
            <Checkbox
              inputId="remember"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.checked ?? false)}
              disabled={isFormDisabled}
            />
            <label htmlFor="remember">Remember me</label>
          </div>

          <Button
            type="submit"
            label={isFormDisabled ? 'Signing in...' : 'Sign In'}
            icon="ms-out ms-out-login"
            className="submit-button"
            disabled={isFormDisabled}
            loading={isFormDisabled}
          />
        </form>

        <div className="auth-footer">
          <p><Link to="/forgot-password">Forgot password?</Link></p>
        </div>
      </Card>
    </div>
  );
};

export default Login;
